import React from "react";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCruises } from "../../store/thunks/fetchCruises";
import { Disclosure } from "@headlessui/react";
import { FunnelIcon } from "@heroicons/react/20/solid";
import shipList from "./shipList";
import { initialFilters } from "./Filters";
import regionList from "./regionList";
import Pagination from "./Pagination";
import DatePicker from "react-datepicker";
import CruiseShipDropdown from "./CruiseShipDropdown";
import RegionDropdown from "./RegionDropdown";
import PortDropdown from "./PortDropdown";

import "react-datepicker/dist/react-datepicker.css";
import "./CruiseFilter.css";

function CruiseFilter({ onPageChange, sortOrder, sort }) {
  const [selectedCruiseId, setSelectedCruiseId] = useState(null);
  // const [selectedCruiseShip, setSelectedCruiseShip] = useState("");
  const [selectedCruiseLine, setSelectedCruiseLine] = useState("");
  const [selectedShip, setSelectedShip] = useState("");
  const [selectedNights, setSelectedNights] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null); //product = category class = room type
  const [excludeSoldOut, setExcludeSoldOut] = useState(true);
  const [minSailDate, setMinSailDate] = useState("");
  const [minSailDateObj, setMinSailDateObj] = useState(null);
  const [maxSailDate, setMaxSailDate] = useState("");
  const [maxSailDateObj, setMaxSailDateObj] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [portList, setPortList] = useState([]);
  const [isMaxDateOpen, setIsMaxDateOpen] = useState(false);
  const [dateSelectError, setDateSelectError] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [overlayTop, setOverlayTop] = useState(0);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const [filters, setFilters] = useState(initialFilters);


  const dispatch = useDispatch();
    const cruises = useSelector((state) => state.cruises.cruisesList.cruises);
  const totalPages = useSelector((state) => state.cruises.totalPages);

  const rowRefs = useMemo(() => {
    return cruises ? cruises.map(() => React.createRef()) : [];
  }, [cruises]);

  useEffect(() => {
    const params = {
      sort: sort,
      sortOrder: sortOrder,
      page: currentPage,
      pageSize: 21,
      cruiseLine: encodeURIComponent(selectedCruiseLine),
      ship: encodeURIComponent(selectedShip),
      excludeSoldOut: excludeSoldOut,
      nights: selectedNights,
      category: selectedCategory,
      minSailDateObj: minSailDateObj,
      maxSailDateObj: maxSailDateObj,
      region: selectedRegion,
      selectedPorts: portList,
    };
    dispatch(fetchCruises(params));
  }, [
    sort,
    sortOrder,
    currentPage,
    excludeSoldOut,
    selectedCruiseLine,
    selectedShip,
    selectedNights,
    selectedCategory,
    minSailDateObj,
    maxSailDateObj,
    selectedRegion,
    portList,
    selectedPorts,
    dispatch,
  ]);

  useEffect(() => {
    if (
      currentPage === 1 &&
      rowRefs[9]?.current &&
      rowRefs[cruises.length - 1]?.current
    ) {
      const topPosition = rowRefs[9]?.current.getBoundingClientRect().top;
      const bottomPosition =
        rowRefs[cruises.length - 1]?.current.getBoundingClientRect().bottom;
      const overlayHeight = bottomPosition - topPosition;

      setOverlayTop(topPosition);
      setOverlayHeight(overlayHeight);
    }
  }, [currentPage, rowRefs, cruises]);

  const onCruiseShipChange = (selectedValue) => {
    setSelectedShip(selectedValue);
    setCurrentPage(1);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Filter Ship - ${selectedValue}`,
    // });
  };

  const handleRegionChange = (selectedValue) => {
    setSelectedRegion(selectedValue);
    console.log("Region selected (from dropdown):", selectedValue);
    // Add or remove any other state updates as per your requirements
    setCurrentPage(1);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Filter Region - ${selectedValue}`,
    // });
  };

  const handlePortChange = (selectedPorts) => {
    setSelectedPorts(selectedPorts);
    setPortList(selectedPorts.map((port) => port.unlocode)); // this maps the selected ports into portList which is to be passed into the thunk
    setCurrentPage(1);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Filter Ports - ${selectedPorts
    //     .map((port) => port.name)
    //     .join(", ")}`,
    // });
  };

  // Handler for the filters (the first 4)
  const onFilterChange = (filterType, filterValue) => {
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Filter ${filterType} - ${filterValue}`,
    // });
    // Update the filter state based on the checkbox change
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [filterType]: prevFilters[filterType].map((option) =>
          option.value === filterValue
            ? { ...option, checked: !option.checked }
            : option
        ),
      };

      // Compute new selected values based on the updated filters
      const newSelectedNights = extractCheckedValues(updatedFilters.nights);
      const newSelectedCategory = extractCheckedValues(
        updatedFilters.categoryClass
      );

      // Update other states
      setSelectedNights(newSelectedNights);
      setSelectedCategory(newSelectedCategory);

      return updatedFilters;
    });
  };

  const extractCheckedValues = (filterArray) => {
    return filterArray
      .filter((option) => option.checked)
      .map((option) => option.value)
      .join(",");
  };

  const clearFilters = () => {
    setSelectedNights("");
    setSelectedCategory("");
    setSelectedCruiseLine("");
    setSelectedShip("");
    setMinSailDate("");
    setMinSailDateObj(null);
    setMaxSailDate("");
    setMaxSailDateObj(null);
    setExcludeSoldOut(false);
    setSelectedRegion("");
    setSelectedPorts([]);
    setPortList([]);

    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        nights: prevFilters.nights.map((option) => ({
          ...option,
          checked: false,
        })),
        categoryClass: prevFilters.categoryClass.map((option) => ({
          ...option,
          checked: false,
        })),
      };
    });
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Cleared All Filters`,
    // });
  };

  // Filter count
  const countActiveFilters = () => {
    let activeFilters = 0;

    // Check filters object for checked filters
    for (let key in filters) {
      // Exclude excludeSoldOut from the loop
      if (key === "excludeSoldOut" || key === "selectedPorts") continue;

      for (let filter of filters[key]) {
        if (filter.checked) activeFilters++;
      }
    }

    // Check other states for selected filters
    if (selectedCruiseLine && selectedCruiseLine !== "") activeFilters++;
    if (selectedShip && selectedShip !== "" && selectedShip !== "All")
      activeFilters++;
    // if (excludeSoldOut) activeFilters++;
    if (minSailDate && minSailDate !== "") activeFilters++;
    if (maxSailDate && maxSailDate !== "") activeFilters++;
    if (selectedRegion && selectedRegion !== "") activeFilters++;

    // Include the count of selected ports as active filters
    activeFilters += selectedPorts.length;

    return activeFilters;
  };

  const uniqueCruiseLines = [
    ...new Set(shipList.map((ship) => ship.cruiseLine)),
  ];

  const uniqueRegions = regionList.map((region) => region.name);

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error("Invalid date received:", date);
      return null;
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // +1 because months are 0-indexed
    const year = date.getFullYear();

    return `${month}-${day}-${year}`;
  };

  const pillCategoryMapping = {
    I: "Inside",
    O: "Oceanview",
    B: "Balcony",
    N: "Neighbourhood",
    S: "Suite",
  };

  const activePillFilters = [
    ...selectedNights
      .split(",")
      .filter((night) => night)
      .map((night) => ({
        value: night,
        label: `${night} Nights`,
        filterType: "nights",
      })),
    ...selectedCategory
      .split(",")
      .filter((category) => category)
      .map((category) => ({
        value: category,
        label: `${pillCategoryMapping[category] || category}`,
        filterType: "categoryClass",
      })),
    ...selectedCruiseLine
      .split(",")
      .filter((CruiseLine) => CruiseLine)
      .map((CruiseLine) => ({
        value: CruiseLine,
        label: `${CruiseLine}`,
        filterType: "cruiseLine",
      })),
    ...selectedShip
      .split(",")
      .filter((Ship) => Ship)
      .map((Ship) => ({
        value: Ship,
        label: `${Ship}`,
        filterType: "cruiseShip",
      })),
    ...minSailDate
      .split(",")
      .filter((minDate) => minDate)
      .map((minDate) => ({
        value: minDate,
        label: `Earliest: ${minDate}`,
        filterType: "earliestDeparture",
      })),
    ...maxSailDate
      .split(",")
      .filter((maxDate) => maxDate)
      .map((maxDate) => ({
        value: maxDate,
        label: `Latest: ${maxDate}`,
        filterType: "latestDeparture",
      })),
    ...selectedRegion
      .split(",")
      .filter((Region) => Region) // Ensure non-empty
      .map((Region) => ({
        value: Region,
        label: `${Region}`,
        filterType: "region",
      })),
    ...selectedPorts.map((port) => ({
      value: port.unlocode,
      label: port.name,
      filterType: "selectedPorts",
    })),
  ];

  const removePillFilter = (filterType, filterValue) => {
    switch (filterType) {
      case "selectedPorts":
        setSelectedPorts((prevPorts) =>
          prevPorts.filter((port) => port.unlocode !== filterValue)
        );
        setPortList((prevPortList) =>
          prevPortList.filter((port) => port !== filterValue)
        );
        break;

      case "region":
        console.log("Removing region", filterValue);
        setSelectedRegion("");
        break;

      case "excludeSoldOut":
        setExcludeSoldOut(false);
        break;

      case "nights":
        setSelectedNights((prev) =>
          prev.replace(filterValue + ",", "").replace(filterValue, "")
        );
        setFilters((prevFilters) => ({
          ...prevFilters,
          nights: prevFilters.nights.map((option) =>
            option.value === filterValue
              ? { ...option, checked: false }
              : option
          ),
        }));
        break;

      case "categoryClass":
        setSelectedCategory((prev) =>
          prev.replace(filterValue + ",", "").replace(filterValue, "")
        );
        setFilters((prevFilters) => ({
          ...prevFilters,
          categoryClass: prevFilters.categoryClass.map((option) =>
            option.value === filterValue
              ? { ...option, checked: false }
              : option
          ),
        }));
        break;

      case "cruiseLine":
        setSelectedCruiseLine((prev) =>
          prev.replace(filterValue + ",", "").replace(filterValue, "")
        );
        break;

      case "cruiseShip":
        setSelectedShip((prev) =>
          prev.replace(filterValue + ",", "").replace(filterValue, "")
        );
        break;

      case "earliestDeparture":
        setMinSailDate("");
        setMinSailDateObj(null);
        break;

      case "latestDeparture":
        setMaxSailDate("");
        setMaxSailDateObj(null);
        break;

      default:
        console.warn(`Unexpected filterType encountered: ${filterType}`);
        break;
    }
  };

  const handleNext = () => {
    setCurrentPage((prevPage) => prevPage + 1);
    onPageChange((prevPage) => prevPage + 1);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Next Page - Page ${currentPage}`,
    // });
  };

  const handlePrev = () => {
    setCurrentPage((prevPage) => prevPage - 1);
    onPageChange((prevPage) => prevPage - 1);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Previous Page - Page ${currentPage}`,
    // });
  };

  const handlePageClick = (clickedPage) => {
    setCurrentPage(clickedPage);
    onPageChange(clickedPage);
    // ReactGA.event({
    //   event: "cruise_search",
    //   category: "Search",
    //   action: `Clicked Page ${clickedPage} `,
    // });
  };

  return (
    <div>
      <div className="relative rounded-xl bg-gray-100" style={{ zIndex: 2 }}>
        <Disclosure
          as="section"
          aria-labelledby="filter-heading"
          className="grid items-center">
          {/* Filter header */}
          <div className="relative col-start-1 row-start-1 py-4">
            <div className="mx-auto flex max-w-7xl space-x-6 divide-x divide-gray-200 px-4 text-sm sm:px-6 lg:px-8">
              <div className="flex flex-col">
                <Disclosure.Button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="group flex items-center font-medium text-gray-700">
                  <FunnelIcon
                    className="mr-2 h-5 w-5 flex-none text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  <div>
                    {countActiveFilters() === 0
                      ? "Filters"
                      : countActiveFilters() === 1
                        ? "1 Filter"
                        : `${countActiveFilters()} Filters`}
                  </div>
                </Disclosure.Button>
                <button
                  onClick={clearFilters}
                  type="button"
                  className="mt-2 text-gray-500 hover:text-gray-700">
                  Clear
                </button>
              </div>

              {/* Active filters */}
              <div className="pl-6">
                <div className="flex flex-wrap gap-2">
                  {activePillFilters.length > 0 &&
                    activePillFilters.map((activeFilter) => (
                      <span
                        key={activeFilter.value}
                        className="inline-flex items-center rounded-full border border-gray-200 bg-white py-1 pl-3 pr-2 text-sm font-medium text-gray-900">
                        <span>{activeFilter.label}</span>
                        <button
                          onClick={() =>
                            removePillFilter(
                              activeFilter.filterType,
                              activeFilter.value
                            )
                          }
                          type="button"
                          className="ml-1 inline-flex h-4 w-4 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                          <span className="sr-only">
                            Remove filter for {activeFilter.label}
                          </span>
                          <svg
                            className="h-2 w-2"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 8 8">
                            <path
                              strokeLinecap="round"
                              strokeWidth="1.5"
                              d="M1 1l6 6m0-6L1 7"
                            />
                          </svg>
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Filter panel */}
          <div>
            <Disclosure.Panel className="border-t border-gray-200 py-6">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Nights */}
                  <fieldset className="space-y-4">
                    <legend className="text-base font-medium text-gray-900">
                      Nights
                    </legend>
                    <div className="space-y-4">
                      {filters.nights.map((option, optionIdx) => (
                        <div
                          key={option.value}
                          className="flex items-center">
                          <input
                            id={`nights-${optionIdx}`}
                            name="nights[]"
                            value={option.value}
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            checked={option.checked}
                            onChange={() =>
                              onFilterChange("nights", option.value)
                            }
                          />
                          <label
                            htmlFor={`nights-${optionIdx}`}
                            className="ml-3 text-sm text-gray-600">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  {/* Category class */}
                  <fieldset className="space-y-4">
                    <legend className="text-base font-medium text-gray-900">
                      Category Class
                    </legend>
                    <div className="space-y-4">
                      {filters.categoryClass.map((option, optionIdx) => (
                        <div
                          key={option.value}
                          className="flex items-center">
                          <input
                            id={`categoryClass-${optionIdx}`}
                            name="categoryClass[]"
                            value={option.value}
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={option.checked}
                            onChange={() =>
                              onFilterChange("categoryClass", option.value)
                            }
                          />
                          <label
                            htmlFor={`categoryClass-${optionIdx}`}
                            className="ml-3 text-sm text-gray-600">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  {/* Sort By Price */}
                  <fieldset className="space-y-4">
                    <legend className="text-base font-medium text-gray-900">
                      Sort By Price
                    </legend>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <input
                          id="price-low-high"
                          name="price-sort"
                          value="low-high" 
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          onChange={() => onFilterChange("priceSort", "low-high")}
                        />
                        <label
                          htmlFor="price-low-high"
                          className="ml-3 text-sm text-gray-600">
                          Lowest to Highest
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="price-high-low"
                          name="price-sort"
                          value="high-low"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          onChange={() => onFilterChange("priceSort", "high-low")}
                        />
                        <label
                          htmlFor="price-high-low"
                          className="ml-3 text-sm text-gray-600">
                          Highest to Lowest
                        </label>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="relative z-10">
                    <CruiseShipDropdown
                      className="w-full"
                      shipList={shipList}
                      onCruiseShipChange={onCruiseShipChange}
                      selectedShip={selectedShip}
                      setSelectedShip={setSelectedShip}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>
                  <div className="relative z-10">
                    <RegionDropdown
                      className="w-full"
                      uniqueRegions={uniqueRegions}
                      onRegionChange={handleRegionChange}
                      setCurrentPage={setCurrentPage}
                      selectedRegion={selectedRegion}
                      setSelectedRegion={setSelectedRegion}
                    />
                  </div>
                  <div className="relative z-10">
                    <PortDropdown
                      selectedPorts={selectedPorts}
                      setSelectedPorts={handlePortChange}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 relative">
                  {/* Earliest departure */}
                  <fieldset>
                    <legend className="text-base font-medium text-gray-900">
                      Earliest departure
                    </legend>
                    <div className="mt-2 relative z-10">
                      <DatePicker
                        selected={minSailDateObj}
                        onChange={(date) => {
                          if (date) {
                            const formattedDate = formatDate(date);
                            setMinSailDate(formattedDate);
                            setMinSailDateObj(date);
                            setMaxSailDate("");
                            setMaxSailDateObj(null);
                          }
                        }}
                        minDate={
                          new Date(new Date().setDate(new Date().getDate() + 2))
                        }
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Select a date"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      {dateSelectError && (
                        <p className="mt-2 text-sm text-red-600">Please set both dates</p>
                      )}
                    </div>
                  </fieldset>

                  {/* Latest departure */}
                  <fieldset>
                    <legend className="text-base font-medium text-gray-900">
                      Latest departure
                    </legend>
                    <div className="mt-2 relative z-10">
                      <DatePicker
                        selected={maxSailDateObj}
                        onChange={(date) => {
                          if (date) {
                            const formattedDate = formatDate(date);
                            setMaxSailDate(formattedDate);
                            setMaxSailDateObj(date);
                          }
                        }}
                        onCalendarOpen={() => setIsMaxDateOpen(true)}
                        onCalendarClose={() => setIsMaxDateOpen(false)}
                        minDate={(() => {
                          const minDate120Days = new Date(
                            new Date().setDate(new Date().getDate() + 120)
                          );
                          if (!minSailDateObj) return minDate120Days;
                          return new Date(minSailDateObj) > minDate120Days
                            ? new Date(minSailDateObj)
                            : minDate120Days;
                        })()}
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Select a date"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      {dateSelectError && minSailDateObj && !maxSailDateObj && (
                        <p className="mt-2 text-sm text-red-600">
                          Max date must be after min date
                        </p>
                      )}
                    </div>
                  </fieldset>
                </div>

              </div>
            </Disclosure.Panel>
          </div>
        </Disclosure>
      </div>
      <div className="mt-4">
        <div className="border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            handleNext={handleNext}
            handlePrev={handlePrev}
            handlePage={handlePageClick}
          />
        </div>
      </div>
    </div>
  );
}

export default CruiseFilter;
